import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, DestroyRef, effect, inject, signal, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { BasicAuthCredentials, IamDivision, UsersIamDivisionChoice } from '@r1-compass-library/models';
import { IamUserService } from '@r1-compass-library/services';
import { LocalStorageService } from '@r1-compass-library/services/local-storage.service';
import { LoginService } from '@r1-compass-library/services/login.service';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';
import { catchError, EMPTY, filter, Observable, switchMap, tap } from 'rxjs';
import { CredentialsUnPwFormComponent } from './credentials-un-pw-form/credentials-un-pw-form.component';
import { SelectDivisionComponent } from './select-division/select-division.component';
import { PageShellComponent } from './page-shell.component';
import { injectQueryParams } from 'ngxtension/inject-query-params';

@Component({
    selector: 'r1-compass-app-login',
    imports: [CredentialsUnPwFormComponent, SelectDivisionComponent, PageShellComponent],
    template: `
        <r1-compass-app-page-shell>
            @if (shownForm() === 'username-password') {
                <r1-compass-app-credentials-un-pw-form
                    (submitLoginForm)="onSignIn($event)"
                    [loginError]="errorMessage()" />
            } @else if (shownForm() === 'division') {
                <r1-compass-app-select-division
                    [divisions]="usersAvailableDivisions()"
                    (selectDivision)="onSelectDivision($event)" />
            }
        </r1-compass-app-page-shell>
    `,
    styleUrl: './login.component.scss',
})
export class LoginComponent {
    readonly #router = inject(Router);
    readonly #route = inject(ActivatedRoute);
    readonly #loginService = inject(LoginService);
    readonly #loggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #userService = inject(IamUserService);
    readonly #divisionsStore = inject(GlobalDivisionsStore);
    readonly #destroyRef = inject(DestroyRef);
    readonly #localStorageService = inject(LocalStorageService);

    shownForm: Signal<'division' | 'username-password'> = computed(() =>
        this.usersAvailableDivisions().length > 1 ? 'division' : 'username-password'
    );
    usersAvailableDivisions: Signal<UsersIamDivisionChoice[]> = this.#loggedInUserStore.usersAvailableDivisions;
    selectedIamDivisionId$ = toObservable(this.#loggedInUserStore.selectedIamDivisionId);

    welcomePageInfo: { title: string; subtitle: string } = {
        title: 'Scout',
        subtitle: 'Compass Playmaker Application',
    };

    userName = injectLocalStorage<string>('username');
    errorMessage = signal<string | undefined>(undefined);
    returnUrl = injectQueryParams('returnUrl');


    iamDivisions$: Observable<IamDivision[]> = toObservable(this.#divisionsStore.allIamDivisions);

    onSignIn(userInfo: BasicAuthCredentials) {
        this.#loginService
            .login(userInfo)
            .pipe(
                tap(res => {
                    this.#loggedInUserStore.setUsersDivisions(res.divs);
                    this.userName.set(userInfo.username);
                    if (res.divs.length === 1) {
                        this.onSelectDivision(res.divs[0]);
                    }
                }),
                catchError((err: HttpErrorResponse) => {
                    this.errorMessage.set(err.error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe(() => {
                this.errorMessage.set(undefined);
            });
    }

    onSelectDivision(selectedDivisionInfo: UsersIamDivisionChoice) {
        const username = this.userName();

        //we first select the division so the jwt access token is set correctly and then set the user so
        //everything downstream is initialized correctly
        this.#loginService
            .selectDivision(selectedDivisionInfo.divisionCode)
            .pipe(
                tap(() => this.#loggedInUserStore.setSelectedIamDivisionId(selectedDivisionInfo.id)),
                switchMap(() => this.#userService.getByUsername(username!)),
                catchError((err: HttpErrorResponse) => {
                    this.errorMessage.set(err.error || 'Failed to select division');
                    return EMPTY;
                }),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe(user => {
                this.errorMessage.set(undefined);
                this.#loggedInUserStore.setUser(user);
                this.#divisionsStore.getAllDivisionData();
                this.subscribeToIamDivisions();
            });
    }

    private readonly routeToPlays = effect(() => {
        const allDivisionConfigs = this.#divisionsStore.allDivisionConfigs();
        const allIamDivisions = this.#divisionsStore.allIamDivisions();
        const jwtClaims = this.#localStorageService.jwtClaims();
        const selectedDivisionId = this.#loggedInUserStore.selectedIamDivisionId();
        const selectedDivisionCode = this.#loggedInUserStore
            .usersAvailableDivisions()
            .find(div => div.id === selectedDivisionId)?.divisionCode;

        if (
            selectedDivisionCode &&
            allDivisionConfigs.length > 0 &&
            allIamDivisions.length > 0 &&
            jwtClaims?.recs?.some(rec => rec.divisionCode === selectedDivisionCode)
        ) {

            if (this.returnUrl()) {
                this.#router.navigateByUrl(this.returnUrl()!);
            } else {
                this.#router.navigate(['/plays'], {
                    queryParams: { folderId: 0 },
                });
            }

            localStorage.removeItem('username');
        }
    });

    private subscribeToIamDivisions() {
        this.iamDivisions$
            .pipe(
                filter(divs => !!divs && !!divs.length),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe(iamDivisions => {
                const divChoices = this.#loggedInUserStore.usersAvailableDivisions();
                divChoices.forEach(choice => {
                    const match = iamDivisions.find(div => div.id === choice.id);
                    if (match) {
                        choice.description = match.description;
                    }
                });
                this.#loggedInUserStore.setUsersDivisions(divChoices);
            });
    }
}
